import { useEffect, useState } from "react";
import { X, Plus, Trash2, Save, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { t } from "@/i18n";

const BLOCK_TYPES = [
  { value: "text", label: "TEXT (explication libre)" },
  { value: "concept", label: "KEY (idée clé, encadré violet)" },
  { value: "tip", label: "TIP (astuce ocre)" },
  { value: "example", label: "EX (exemple arabe : ar/translit/fr/note)" },
];

const LEVELS = ["base", "A1", "A2", "B1", "B2", "C1", "C2"];
const SKILLS = ["alphabet", "vocabulary", "grammar", "conjugation", "reading", "writing"];

function BlockEditor({ blocks, setBlocks }) {
  const update = (i, patch) => setBlocks(blocks.map((b, j) => j === i ? { ...b, ...patch } : b));
  const remove = (i) => setBlocks(blocks.filter((_, j) => j !== i));
  const add = (type) => {
    const base = { type };
    if (type === "example") Object.assign(base, { ar: "", translit: "", fr: "", note: "" });
    else base.value = "";
    setBlocks([...blocks, base]);
  };
  const moveUp = (i) => {
    if (i === 0) return;
    const arr = [...blocks]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setBlocks(arr);
  };
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => (
        <div key={i} className="border rounded-xl p-3 bg-background">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => moveUp(i)} className="text-muted-foreground hover:text-foreground text-xs" title={t("lessoneditor.monter")}>▲</button>
            <select value={b.type} onChange={(e) => update(i, { type: e.target.value })} className="px-2 py-1 rounded border bg-background text-xs">
              {BLOCK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span className="text-xs text-muted-foreground ml-auto">{t("lessoneditor.bloc")} {i + 1}</span>
            <button onClick={() => remove(i)} className="text-destructive p-1"><Trash2 size={13} /></button>
          </div>
          {b.type === "example" ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <input placeholder={t("lessoneditor.ar_arabe")} dir="rtl" value={b.ar || ""} onChange={(e) => update(i, { ar: e.target.value })} className="px-2 py-1.5 rounded border bg-background arabic text-lg" />
              <input placeholder={t("lessoneditor.translit")} value={b.translit || ""} onChange={(e) => update(i, { translit: e.target.value })} className="px-2 py-1.5 rounded border bg-background italic text-sm" />
              <input placeholder={t("lessoneditor.fr_ou_en")} value={b.fr || ""} onChange={(e) => update(i, { fr: e.target.value })} className="px-2 py-1.5 rounded border bg-background text-sm" />
              <input placeholder={t("lessoneditor.note")} value={b.note || ""} onChange={(e) => update(i, { note: e.target.value })} className="px-2 py-1.5 rounded border bg-background text-sm" />
            </div>
          ) : (
            <textarea rows={2} placeholder={t("lessoneditor.contenu")} value={b.value || ""} onChange={(e) => update(i, { value: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-background text-sm" />
          )}
        </div>
      ))}
      <div className="flex gap-2 flex-wrap">
        {BLOCK_TYPES.map((t) => (
          <button key={t.value} onClick={() => add(t.value)}
            className="text-xs px-2 py-1 rounded-lg border hover:bg-secondary flex items-center gap-1">
            <Plus size={12} /> {t.value.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

function QuizEditor({ quiz, setQuiz }) {
  const update = (i, patch) => setQuiz(quiz.map((q, j) => j === i ? { ...q, ...patch } : q));
  const remove = (i) => setQuiz(quiz.filter((_, j) => j !== i));
  const add = () => setQuiz([...quiz, { prompt_fr: "", prompt_en: "", options_fr: ["", "", "", ""], options_en: ["", "", "", ""], answer: 0, difficulty: 2, explain_fr: "", explain_en: "" }]);
  const setOpt = (i, which, oi, v) => setQuiz(quiz.map((q, j) => j === i ? { ...q, [which]: q[which].map((o, k) => k === oi ? v : o) } : q));
  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border rounded-xl p-3 bg-background space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Q{i + 1}</span>
            <select value={q.answer} onChange={(e) => update(i, { answer: parseInt(e.target.value, 10) })} className="px-2 py-1 rounded border bg-background text-xs">
              {[0, 1, 2, 3].map((k) => <option key={k} value={k}>{t("lessoneditor.bonne_reponse")} {k + 1}</option>)}
            </select>
            <input type="number" min={1} max={5} value={q.difficulty} onChange={(e) => update(i, { difficulty: parseInt(e.target.value, 10) || 2 })}
              className="w-16 px-2 py-1 rounded border bg-background text-xs" title={t("lessoneditor.difficulte_1_5")} />
            <button onClick={() => remove(i)} className="ml-auto text-destructive p-1"><Trash2 size={13} /></button>
          </div>
          <input placeholder={t("lessoneditor.question_fr")} value={q.prompt_fr} onChange={(e) => update(i, { prompt_fr: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-background text-sm" />
          <input placeholder={t("lessoneditor.question_en")} value={q.prompt_en || ""} onChange={(e) => update(i, { prompt_en: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-background text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-muted-foreground mb-1">{t("lessoneditor.options_fr")}</div>
              {(q.options_fr || []).map((o, oi) => (
                <input key={oi} placeholder={`Opt ${oi + 1}`} value={o} onChange={(e) => setOpt(i, "options_fr", oi, e.target.value)} className="w-full px-2 py-1 rounded border bg-background text-sm mb-1" />
              ))}
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">{t("lessoneditor.options_en")}</div>
              {(q.options_en || ["", "", "", ""]).map((o, oi) => (
                <input key={oi} placeholder={`Opt ${oi + 1}`} value={o} onChange={(e) => setOpt(i, "options_en", oi, e.target.value)} className="w-full px-2 py-1 rounded border bg-background text-sm mb-1" />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder={t("lessoneditor.explication_fr_optionnel")} value={q.explain_fr || ""} onChange={(e) => update(i, { explain_fr: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-background text-xs" />
            <input placeholder={t("lessoneditor.explanation_en")} value={q.explain_en || ""} onChange={(e) => update(i, { explain_en: e.target.value })} className="w-full px-2 py-1.5 rounded border bg-background text-xs" />
          </div>
        </div>
      ))}
      <button onClick={add} className="px-3 py-2 rounded-xl border font-medium text-sm flex items-center gap-1 hover:bg-secondary">
        <Plus size={14} /> {t("lessoneditor.ajouter_une_question")}
      </button>
    </div>
  );
}

export default function LessonEditor({ lessonId, onClose, onSaved }) {
  const [lesson, setLesson] = useState(null);
  const [tab, setTab] = useState("meta");

  useEffect(() => {
    if (!lessonId) { setLesson(null); return; }
    api.get(`/admin/lessons/${lessonId}/full`).then((r) => setLesson(r.data));
  }, [lessonId]);

  if (!lessonId) return null;
  if (!lesson) return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card p-6 rounded-2xl">{t("lessoneditor.chargement")}</div>
    </div>
  );

  const save = async () => {
    const payload = {
      title_fr: lesson.title_fr, title_en: lesson.title_en,
      term_ar: lesson.term_ar || "", translit: lesson.translit || "",
      minutes: lesson.minutes || 4, skill: lesson.skill || "grammar",
      level: lesson.level || "A1", module_id: lesson.module_id || "",
      order: lesson.order || 0,
      objective_fr: lesson.objective_fr || "", objective_en: lesson.objective_en || "",
      blocks_fr: lesson.blocks_fr || [], blocks_en: lesson.blocks_en || [],
      quiz: lesson.quiz || [],
    };
    try {
      await api.put(`/admin/lessons/${lessonId}`, payload);
      toast.success("Leçon enregistrée.");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Erreur d'enregistrement.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-2xl border w-full max-w-4xl my-8 shadow-2xl">
        <div className="flex items-center gap-3 p-5 border-b sticky top-0 bg-card z-10 rounded-t-2xl">
          <h2 className="font-head text-xl font-bold flex-1 truncate">{t("lessoneditor.editer_la_lecon")} {lesson.title_fr}</h2>
          <button onClick={save} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center gap-2"><Save size={16} /> {t("lessoneditor.enregistrer")}</button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary"><X size={18} /></button>
        </div>

        <div className="flex gap-1 border-b px-5">
          {["meta", "blocks_fr", "blocks_en", "quiz"].map((k) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-3 text-sm font-medium border-b-2 ${tab === k ? "border-primary text-primary" : "border-transparent hover:bg-secondary/50"}`}>
              {k === "meta" && "Métadonnées"}
              {k === "blocks_fr" && "Contenu FR"}
              {k === "blocks_en" && "Contenu EN"}
              {k === "quiz" && `Questions (${(lesson.quiz || []).length})`}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "meta" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs">{t("lessoneditor.titre_fr")}</label><input value={lesson.title_fr || ""} onChange={(e) => setLesson({ ...lesson, title_fr: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
              <div><label className="text-xs">{t("lessoneditor.title_en")}</label><input value={lesson.title_en || ""} onChange={(e) => setLesson({ ...lesson, title_en: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
              <div><label className="text-xs">{t("lessoneditor.terme_ar")}</label><input dir="rtl" value={lesson.term_ar || ""} onChange={(e) => setLesson({ ...lesson, term_ar: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background arabic" /></div>
              <div><label className="text-xs">{t("lessoneditor.translitteration")}</label><input value={lesson.translit || ""} onChange={(e) => setLesson({ ...lesson, translit: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
              <div><label className="text-xs">{t("lessoneditor.competence")}</label>
                <select value={lesson.skill || "grammar"} onChange={(e) => setLesson({ ...lesson, skill: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background">
                  {SKILLS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="text-xs">{t("lessoneditor.niveau_cecrl")}</label>
                <select value={lesson.level || "A1"} onChange={(e) => setLesson({ ...lesson, level: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background">
                  {LEVELS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="text-xs">{t("lessoneditor.minutes_estimees")}</label><input type="number" min={1} max={60} value={lesson.minutes || 4} onChange={(e) => setLesson({ ...lesson, minutes: parseInt(e.target.value, 10) || 4 })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
              <div><label className="text-xs">{t("lessoneditor.ordre_dans_le_niveau")}</label><input type="number" value={lesson.order || 0} onChange={(e) => setLesson({ ...lesson, order: parseInt(e.target.value, 10) || 0 })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
              <div className="sm:col-span-2"><label className="text-xs">{t("lessoneditor.objectif_fr")}</label><input value={lesson.objective_fr || ""} onChange={(e) => setLesson({ ...lesson, objective_fr: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
              <div className="sm:col-span-2"><label className="text-xs">{t("lessoneditor.objective_en")}</label><input value={lesson.objective_en || ""} onChange={(e) => setLesson({ ...lesson, objective_en: e.target.value })} className="w-full px-3 py-2 rounded-xl border bg-background" /></div>
            </div>
          )}
          {tab === "blocks_fr" && <BlockEditor blocks={lesson.blocks_fr || []} setBlocks={(b) => setLesson({ ...lesson, blocks_fr: b })} />}
          {tab === "blocks_en" && <BlockEditor blocks={lesson.blocks_en || []} setBlocks={(b) => setLesson({ ...lesson, blocks_en: b })} />}
          {tab === "quiz" && <QuizEditor quiz={lesson.quiz || []} setQuiz={(q) => setLesson({ ...lesson, quiz: q })} />}
        </div>
      </div>
    </div>
  );
}
